import { hasHiddenInstanceInRange } from '@/features/events/lib/eventVisibility.lib';
import type { DateTimeRange } from '@/features/events/lib/range.lib';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import { useShowHiddenEvents } from '@/features/planner/hooks/useShowHiddenEvents';
import { Icon, IconButton, Tooltip } from '@/ui';

type Props = {
  experience: PageExperience;
  /** Active date range of the view; scopes which hidden events count. */
  range?: DateTimeRange;
};

export const ShowHiddenEventsToggle = ({ experience, range }: Props) => {
  const { isToggleEnabled, isShowingHidden, setShowHidden } =
    useShowHiddenEvents(experience);
  const { allEvents } = usePageExperienceContext();

  // Only hidden occurrences within the active range are relevant — one hidden in
  // a month the picker isn't showing shouldn't surface the control, and neither
  // should a series that merely has a hidden occurrence somewhere else. Without a
  // range (page-type views), every hidden occurrence counts.
  const hasHiddenInRange = allEvents.some((event) =>
    hasHiddenInstanceInRange(event, range)
  );

  // Nothing to reveal — but while show mode is active the control must stay
  // available so the user can always toggle back out.
  if (!isToggleEnabled || (!isShowingHidden && !hasHiddenInRange)) {
    return null;
  }

  const label = isShowingHidden ? 'Hide hidden events' : 'Show hidden events';

  return (
    <Tooltip label={label}>
      <IconButton
        aria-label={label}
        aspectRatio={1}
        icon={<Icon icon={isShowingHidden ? 'EyeOff' : 'Eye'} size={4} />}
        isActive={isShowingHidden}
        size="ah"
        variant="ahSecondary"
        onClick={() => setShowHidden(!isShowingHidden)}
      />
    </Tooltip>
  );
};
